"use client";

import { useEffect, useState } from "react";
import {
  fetchAcceleratorCartCount,
  subscribeToCartUpdates,
} from "@/utils/accelerator-cart.utils";

/**
 * Reads the storefront cart count for the header. The count lives in the
 * accelerator session rather than in this app, so it is fetched after mount
 * when enabled and refreshed whenever something reports a cart change.
 *
 * Returns null until a count is known, so the header can render nothing rather
 * than a misleading zero.
 */
export function useAcceleratorCartCount(
  country: string | undefined,
  language: string | undefined,
  enabled: boolean
): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!(enabled && country && language)) {
      setCount(null);
      return;
    }

    setCount(null);
    let isActive = true;
    let latestRefreshId = 0;

    async function refresh() {
      const refreshId = ++latestRefreshId;

      try {
        const nextCount = await fetchAcceleratorCartCount(
          country as string,
          language as string
        );

        if (isActive && refreshId === latestRefreshId) {
          setCount(nextCount);
        }
      } catch {
        if (isActive && refreshId === latestRefreshId) {
          setCount(null);
        }
      }
    }

    refresh();
    const unsubscribe = subscribeToCartUpdates(refresh);

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [country, language, enabled]);

  return count;
}
